import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  LocalShipping,
  CheckCircle,
  Cancel,
  Assignment
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';

const PurchaseOrders = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formData, setFormData] = useState({
    batch_id: '',
    supplier_name: '',
    category: '',
    item_type: '',
    quantity: '',
    unit_price: '',
    total_amount: '',
    description: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    notes: ''
  });

  const categories = ['Food', 'Medicine', 'Equipment', 'Transportation', 'Labor', 'Housing', 'Vitamins', 'Supplements', 'Other'];
  const statuses = ['Pending', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled'];

  const loadPurchaseOrders = async () => {
    try {
      const response = await fetch('/api/purchase-orders');
      const data = await response.json();
      setPurchaseOrders(data);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await fetch('/api/batches');
      const data = await response.json();
      setBatches(data.filter(batch => batch.status === 'Active'));
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadPurchaseOrders(), loadBatches()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleAddOrder = async () => {
    try {
      const response = await fetch('/api/purchase-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await loadPurchaseOrders();
        setAddDialogOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        alert('Error creating purchase order: ' + error.error);
      }
    } catch (error) {
      console.error('Error creating purchase order:', error);
      alert('Error creating purchase order');
    }
  };

  const handleUpdateStatus = async (orderId, status, actualDeliveryDate = null) => {
    try {
      const response = await fetch(`/api/purchase-orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status, 
          actual_delivery_date: actualDeliveryDate 
        }),
      });

      if (response.ok) {
        await loadPurchaseOrders();
      } else {
        const error = await response.json();
        alert('Error updating status: ' + error.error);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };

  const handleConvertToExpenditure = async (orderId) => {
    if (!window.confirm('Convert this purchase order to expenditure? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/purchase-orders/${orderId}/convert-to-expenditure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        await loadPurchaseOrders();
        alert('Purchase order converted to expenditure successfully!');
      } else {
        const error = await response.json();
        alert('Error converting: ' + error.error);
      }
    } catch (error) {
      console.error('Error converting purchase order:', error);
      alert('Error converting purchase order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) {
      return;
    }

    try {
      const response = await fetch(`/api/purchase-orders/${orderId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await loadPurchaseOrders();
      } else {
        const error = await response.json();
        alert('Error deleting purchase order: ' + error.error);
      }
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      alert('Error deleting purchase order');
    }
  };

  const resetForm = () => {
    setFormData({
      batch_id: '',
      supplier_name: '',
      category: '',
      item_type: '',
      quantity: '',
      unit_price: '',
      total_amount: '',
      description: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      notes: ''
    });
  };

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Auto-calculate total amount if quantity and unit_price are provided
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = parseFloat(newFormData.quantity) || 0;
      const unitPrice = parseFloat(newFormData.unit_price) || 0;
      if (quantity > 0 && unitPrice > 0) {
        newFormData.total_amount = (quantity * unitPrice).toString();
      }
    }
    
    setFormData(newFormData);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Confirmed': return 'info';
      case 'Shipped': return 'primary';
      case 'Delivered': return 'success';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return <Typography>Loading purchase orders...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialogOpen(true)}
        >
          New Purchase Order
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {purchaseOrders.filter(po => po.status === 'Pending').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pending Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary.main">
                {purchaseOrders.filter(po => po.status === 'Shipped').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                In Transit
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {purchaseOrders.filter(po => po.status === 'Delivered' && !po.converted_to_expenditure).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Ready to Convert
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {formatCurrency(purchaseOrders.reduce((sum, po) => sum + parseFloat(po.total_amount || 0), 0))}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.order_number}</TableCell>
                    <TableCell>
                      {order.batch_code ? (
                        <Chip
                          label={order.batch_code}
                          size="small"
                          color={order.batch_type === 'Layers' ? 'success' : 'warning'}
                        />
                      ) : 'No batch'}
                    </TableCell>
                    <TableCell>{order.supplier_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {order.category} - {order.item_type}
                      </Typography>
                      {order.quantity && order.unit_price && (
                        <Typography variant="caption" color="textSecondary">
                          {order.quantity} × {formatCurrency(order.unit_price)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                    <TableCell>{new Date(order.order_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {order.expected_delivery_date 
                        ? new Date(order.expected_delivery_date).toLocaleDateString()
                        : 'Not set'
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {order.status !== 'Delivered' && order.status !== 'Cancelled' && (
                          <>
                            {order.status === 'Pending' && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleUpdateStatus(order.id, 'Confirmed')}
                                title="Mark as Confirmed"
                              >
                                <CheckCircle />
                              </IconButton>
                            )}
                            {(order.status === 'Confirmed' || order.status === 'Pending') && (
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleUpdateStatus(order.id, 'Shipped')}
                                title="Mark as Shipped"
                              >
                                <LocalShipping />
                              </IconButton>
                            )}
                            {order.status === 'Shipped' && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleUpdateStatus(order.id, 'Delivered', new Date().toISOString().split('T')[0])}
                                title="Mark as Delivered"
                              >
                                <Assignment />
                              </IconButton>
                            )}
                          </>
                        )}
                        
                        {order.status === 'Delivered' && !order.converted_to_expenditure && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleConvertToExpenditure(order.id)}
                          >
                            Convert to Expenditure
                          </Button>
                        )}
                        
                        {order.converted_to_expenditure && (
                          <Chip label="Converted" color="success" size="small" />
                        )}
                        
                        {order.status !== 'Delivered' && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteOrder(order.id)}
                            title="Delete Order"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {purchaseOrders.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No purchase orders found. Create your first purchase order to get started.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Purchase Order Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Purchase Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Batch (Optional)</InputLabel>
                <Select
                  value={formData.batch_id}
                  label="Batch (Optional)"
                  onChange={(e) => handleInputChange('batch_id', e.target.value)}
                >
                  <MenuItem value="">No specific batch</MenuItem>
                  {batches.map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      {batch.batch_code} - {batch.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={formData.supplier_name}
                onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Type"
                value={formData.item_type}
                onChange={(e) => handleInputChange('item_type', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Unit Price (TZS)"
                type="number"
                value={formData.unit_price}
                onChange={(e) => handleInputChange('unit_price', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Total Amount (TZS)"
                type="number"
                value={formData.total_amount}
                onChange={(e) => handleInputChange('total_amount', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Order Date"
                type="date"
                value={formData.order_date}
                onChange={(e) => handleInputChange('order_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Delivery Date"
                type="date"
                value={formData.expected_delivery_date}
                onChange={(e) => handleInputChange('expected_delivery_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddOrder} variant="contained">
            Create Purchase Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrders;
